import React, { useEffect, useState } from 'react';
import { aopApi, workflowLabel } from '../aopApi';

// Read-only runtime configuration viewer. AOP configuration is env-managed
// (.env on the server, read at worker/app startup) — this view shows the
// actual values from /api/v1/aop/config rather than offering editable
// controls the backend could not honor.

const SECTIONS = [
  {
    id: 'write-policy',
    label: 'Write Policy',
    description: 'How agent write operations to external systems (Salesforce, CSD, Figma, Slack) are controlled.',
  },
  {
    id: 'environment',
    label: 'Environment',
    description: 'Runtime mode, dry-run controls, and model provider flags as currently loaded from the server environment.',
  },
  {
    id: 'execution',
    label: 'Execution',
    description: 'Post-approval execution of pending actions: schedules and live-write target allowlist.',
  },
  {
    id: 'schedules',
    label: 'Schedules',
    description: 'Repeatable jobs the AOP worker registers on startup (server time).',
  },
  {
    id: 'instruction-sources',
    label: 'Instruction Sources',
    description: 'Prompt and skill files loaded by AOP agents and shown in the markdown readers.',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'Presence of credentials for external services (values never leave the server).',
  },
];

function ValueRow({ label, value, color = 'var(--orange)' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
      <code style={{ color: 'var(--blue)', fontFamily: 'monospace', flexShrink: 0 }}>{label}</code>
      <code style={{ color, fontFamily: 'monospace', textAlign: 'right' }}>{value}</code>
    </div>
  );
}

function StateBadge({ on, onLabel = 'Enabled', offLabel = 'Disabled', onColor = 'var(--green)' }) {
  return (
    <span className="aop-badge" style={on
      ? { background: 'rgba(52,199,89,0.12)', color: onColor }
      : { background: 'rgba(156,163,197,0.12)', color: 'var(--text-sec)' }}>
      {on ? onLabel : offLabel}
    </span>
  );
}

export default function Settings() {
  const [activeSection, setActiveSection] = useState('write-policy');
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    aopApi
      .config()
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load configuration');
        setConfig(res.config);
        setError(null);
      })
      .catch((err) => setError(err.message));
  }, []);

  const renderSection = () => {
    if (!config) return <div className="aop-empty">Loading configuration…</div>;

    switch (activeSection) {
      case 'write-policy':
        return (
          <div>
            <div className="aop-settings-group-label">Active Policy</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
              <span className="aop-badge completed">
                {config.writePolicy === 'human_approval' ? 'Human Approval' : config.writePolicy}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-sec)' }}>
                Every external write becomes a pending action requiring explicit approval.
              </span>
            </div>
            <div className="aop-settings-divider" />
            <div className="aop-settings-group-label">Guarantees</div>
            {[
              ['Audit logging', 'Every workflow step, tool call, and approval decision is recorded in AopAuditEvent.'],
              ['Decision finality', 'Approve/reject decisions are final; repeat decisions are rejected.'],
              ['Token budget guard', config.monthlyTokenBudget
                ? `Real model calls refuse once ${config.monthlyTokenBudget.toLocaleString()} tokens are used in a month.`
                : 'No monthly token budget set (AOP_MONTHLY_TOKEN_BUDGET).'],
            ].map(([label, detail]) => (
              <div key={label} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-sec)', marginTop: 2 }}>{detail}</div>
              </div>
            ))}
          </div>
        );

      case 'environment':
        return (
          <div>
            <div className="aop-settings-group-label">Runtime Flags</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '6px 0 14px' }}>
              <StateBadge on={config.enabled} onLabel="Worker Enabled" offLabel="Worker Disabled (AOP_ENABLED=0)" />
              <StateBadge on={config.dryRun} onLabel="Dry Run" offLabel="LIVE MODE" onColor="var(--orange)" />
              <StateBadge on={config.allowAnthropicCalls} onLabel="Anthropic Allowed" offLabel="Anthropic Blocked" />
              <StateBadge on={config.allowOpenAiCalls} onLabel="OpenAI Allowed" offLabel="OpenAI Blocked" />
            </div>
            <div className="aop-settings-group-label">Current Values</div>
            <ValueRow label="AOP_MODEL_PROVIDER" value={config.modelProvider} />
            <ValueRow label="AOP_DEFAULT_MODEL" value={config.defaultModel || '(provider default)'} />
            <ValueRow label="AOP_STRONG_MODEL" value={config.strongModel || '(unset)'} />
            <ValueRow label="AOP_MONTHLY_TOKEN_BUDGET" value={config.monthlyTokenBudget ? String(config.monthlyTokenBudget) : '(unset)'} />
            <ValueRow label="AOP_POLL_INTERVAL_MS" value={String(config.pollIntervalMs)} />
            <ValueRow label="AOP_APPROVER_EMAIL" value={config.approverEmail || '(unset)'} />
            <ValueRow label="MCP_TRANSPORT" value={config.mcpTransport} />
            <ValueRow label="NODE_ENV" value={config.nodeEnv} />
            <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 14, lineHeight: 1.5 }}>
              Configuration is env-managed: edit <code>.env</code> and restart the server/worker to change these values.
            </p>
          </div>
        );

      case 'execution':
        return (
          <div>
            <div className="aop-settings-group-label">Post-Approval Execution</div>
            <p style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.6, margin: '6px 0 14px' }}>
              A recurring worker job executes human-approved pending actions.
              {config.dryRun
                ? ' Dry run is on: approved actions are marked dry_run with an audit record, nothing executes.'
                : ' Live mode: targets below execute through real adapters; everything else is marked manual_required.'}
            </p>
            <ValueRow label="AOP_EXECUTE_TARGETS" value={config.executeTargets.join(', ')} />
            <ValueRow label="AOP_SLACK_CHANNEL" value={config.slackChannel || '(unset — Slack sends will fail live)'} />
            <div className="aop-settings-divider" />
            <div className="aop-settings-group-label">Execution Outcomes</div>
            {[
              ['executed', 'Adapter ran the approved write against the target system.'],
              ['dry_run', 'Dry-run mode: recorded what would have happened, no external write.'],
              ['manual_required', 'No adapter or target not allowlisted — a human carries out the action.'],
              ['failed', 'Adapter error; recorded on the action and in the audit stream.'],
            ].map(([status, detail]) => (
              <div key={status} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <code style={{ fontSize: 12, color: 'var(--blue)' }}>{status}</code>
                <div style={{ fontSize: 11, color: 'var(--text-sec)', marginTop: 2 }}>{detail}</div>
              </div>
            ))}
          </div>
        );

      case 'schedules':
        return (
          <div>
            <div className="aop-settings-group-label">Repeatable Jobs</div>
            {config.schedules.map((schedule) => (
              <div key={schedule.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                    {schedule.name === 'aop-execute-approved-actions' ? 'Approved-action executor' : workflowLabel(schedule.name)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{schedule.name}</div>
                </div>
                <code style={{ fontSize: 12, color: 'var(--orange)', fontFamily: 'monospace' }}>{schedule.cron}</code>
              </div>
            ))}
            <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 14 }}>
              Registered by the worker at startup; runs only while <code>AOP_ENABLED=1</code>.
            </p>
          </div>
        );

      case 'instruction-sources':
        return (
          <div>
            <div className="aop-settings-group-label">Markdown Sources</div>
            {[
              ['Agent skills', 'skills/*/SKILL.md', 'Loaded as canonical agent system instructions.'],
              ['Skill references', 'skills/*/references/*.md', 'Used as supporting playbooks and examples.'],
              ['Prompt libraries', 'prompts/**/*.md', 'Shared generation rules, templates, and output contracts.'],
              ['Version marker', 'git commit hash', 'Recorded on each AopAgentRun when a skill is loaded.'],
            ].map(([label, src, detail]) => (
              <div key={label} className="aop-settings-source-row">
                <div>
                  <div className="aop-settings-source-label">{label}</div>
                  <div className="aop-settings-source-detail">{detail}</div>
                </div>
                <code>{src}</code>
              </div>
            ))}
          </div>
        );

      case 'integrations':
        return (
          <div>
            <div className="aop-settings-group-label">Credential Presence</div>
            {[
              ['Salesforce', config.integrations.salesforce],
              ['Slack bot token', config.integrations.slackBot],
              ['Slack signing secret', config.integrations.slackSigningSecret],
              ['OpenAI', config.integrations.openai],
              ['Anthropic', config.integrations.anthropic],
              ['Google OAuth', config.integrations.google],
              ['MCP bridge token', config.integrations.mcpBridgeToken],
              ['MongoDB connection', config.integrations.mongo],
            ].map(([name, present]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: present ? 'var(--green)' : 'var(--text-dim)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1 }}>{name}</span>
                <span style={{ fontSize: 11, color: present ? 'var(--green)' : 'var(--text-dim)' }}>
                  {present ? 'Configured' : 'Not configured'}
                </span>
              </div>
            ))}
            <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 14 }}>
              Presence only — credential values never leave the server.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Settings</h1>
          <p className="aop-page-subtitle">AOP runtime configuration — live values, read-only (env-managed)</p>
        </div>
        <span className="aop-badge" style={{ background: 'rgba(74,143,247,0.12)', color: 'var(--blue)' }}>Read-only</span>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="aop-settings-layout">
        <nav className="aop-settings-nav">
          {SECTIONS.map(s => (
            <div
              key={s.id}
              className={`aop-settings-nav-item${activeSection === s.id ? ' active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              {s.label}
            </div>
          ))}
        </nav>

        <div className="aop-settings-body">
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
              {SECTIONS.find(s => s.id === activeSection)?.label}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-sec)', marginTop: 4, lineHeight: 1.5 }}>
              {SECTIONS.find(s => s.id === activeSection)?.description}
            </p>
          </div>
          {renderSection()}
        </div>
      </div>
    </div>
  );
}
